import axiosInstance from "./axiosInstance";

// Presigned download URLs are minted with a 1h expiry by the backend, so a
// shorter client TTL keeps every cached URL comfortably usable.
const TTL_MS = 45 * 60 * 1000;

const urlCache = new Map();
const inFlight = new Map();

export function parseVerificationIds(verificationId) {
    if (!verificationId) {
        return [];
    }
    return String(verificationId)
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);
}

export function getCachedImageUrl(key) {
    if (!key) {
        return null;
    }
    const cached = urlCache.get(key);
    if (cached && cached.expiresAt > Date.now()) {
        return cached.url;
    }
    return null;
}

// Resolves an S3 key to a presigned URL, sharing one request across every
// component that asks for the same key.
export function getImageUrl(key) {
    if (!key) {
        return Promise.resolve(null);
    }

    const cached = getCachedImageUrl(key);
    if (cached) {
        return Promise.resolve(cached);
    }

    const pending = inFlight.get(key);
    if (pending) {
        return pending;
    }

    const request = axiosInstance
        .get(`/api/v1/images/download-presigned-url?key=${encodeURIComponent(key)}`)
        .then((response) => {
            const url = response?.data?.url || null;
            if (url) {
                urlCache.set(key, { url, expiresAt: Date.now() + TTL_MS });
            }
            return url;
        })
        .catch((error) => {
            console.error(`Could not load verification image ${key}`, error);
            return null;
        })
        .finally(() => {
            inFlight.delete(key);
        });

    inFlight.set(key, request);
    return request;
}

export function getImageUrls(keys) {
    return Promise.all(keys.map((key) => getImageUrl(key)));
}

// Seeds the cache with a local object URL so a freshly uploaded file renders
// without a round trip to fetch its presigned URL.
export function primeImageUrl(key, url) {
    if (!key || !url) {
        return;
    }
    urlCache.set(key, { url, expiresAt: Date.now() + TTL_MS });
}
